/*
 * Validates a ?redirect= target before it is navigated to after login.
 *
 * Two things go wrong with an unchecked value:
 *
 *   - A protocol-relative path ("//chats") is resolved by the browser against
 *     the scheme, not this app, so it becomes a different origin. React Router
 *     hands it to history.pushState, which refuses a cross-origin URL and
 *     throws a SecurityError. Thrown from inside the login handler's try block,
 *     that surfaced as "Login failed" on a login that had actually succeeded.
 *     "?redirect=%2F/chats?threadId=12" decodes to exactly that, because only
 *     "&" separates query parameters - a "?" inside a value is part of it.
 *
 *   - An absolute URL ("https://example.com") would send a freshly signed-in
 *     user straight off the site, which is an open redirect.
 *
 * So the only thing accepted is a path inside this app: one leading slash, and
 * nothing that a browser would read as a host.
 */
const DEFAULT_TARGET = '/dashboard';

export const safeRedirect = (raw, fallback = DEFAULT_TARGET) => {
  if (!raw) return fallback;

  // URLSearchParams.get() has already decoded once; this second pass matches
  // what the login pages did before, for links that were encoded twice.
  let value = String(raw);
  try {
    value = decodeURIComponent(value);
  } catch (e) {
    // Malformed escapes - keep what we were given and let the checks below rule.
  }

  value = value.trim();

  // Tabs and newlines inside the path are stripped by the browser before it
  // resolves the URL, so "/\t/evil.com" passes every check below and then
  // becomes "//evil.com" - the SecurityError case above, by another route.
  // eslint-disable-next-line no-control-regex -- matching control characters is the point
  if (/[\u0000-\u001F\u007F]/.test(value)) return fallback;

  if (!value.startsWith('/')) return fallback; // absolute URL, or a bare word
  if (value === '/') return fallback; // the guard sends "/" for the root
  // "//host" is protocol-relative; browsers normalise "/\" to the same thing.
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback;

  return value;
};

// The login page, carrying the redirect along when there is a real one. Used by
// the "Back to Login" links, which used to go to "/" - the guard turns that into
// /login?redirect=%2F and the page the client was heading for was lost.
export const loginPath = (raw) => {
  const target = safeRedirect(raw, null);
  return target ? `/login?redirect=${encodeURIComponent(target)}` : '/login';
};

/*
 * Forgot password breaks the chain of ?redirect= links: the reset link arrives
 * by email and usually opens in a new tab, so nothing in its URL knows where the
 * client was heading. The forgot-password page leaves the target here, and the
 * reset page picks it up for the login it sends them to.
 *
 * localStorage, not sessionStorage, because the reset link opens a new tab. An
 * hour is plenty for a reset email. Anything read back goes through safeRedirect
 * again - storage is not trusted input. If storage is unavailable (private mode,
 * blocked site data) the client simply lands on the dashboard, as before.
 */
const REMEMBER_KEY = 'cp_redirect_after_reset';
const REMEMBER_FOR_MS = 60 * 60 * 1000;

export const rememberRedirect = (raw) => {
  const target = safeRedirect(raw, null);
  try {
    if (target) {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify({ target, at: Date.now() }));
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  } catch (e) {
    // Storage unavailable - fall back to the dashboard after the reset.
  }
};

// Reads the remembered target and forgets it, so it is used at most once.
export const takeRememberedRedirect = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null');
    localStorage.removeItem(REMEMBER_KEY);
    if (!stored || Date.now() - stored.at > REMEMBER_FOR_MS) return null;
    return safeRedirect(stored.target, null);
  } catch (e) {
    return null;
  }
};

export default safeRedirect;
